/**
 * VariableManager Component
 *
 * Manages flow variables.
 * Allows adding, editing, and deleting variables.
 *
 * @component
 */

import React, { useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  List,
  Database,
  Layers,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  X,
  Check
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardModal,
  StandardInput,
  StandardSelect,
  StandardTextarea,
  StandardCheckbox,
  StandardEmptyState,
  useToast
} from '../../standard';
import type { FlowVariable, VariableType } from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface VariableManagerProps {
  variables: FlowVariable[];
  readOnly?: boolean;
  onAdd?: (variable: FlowVariable) => void;
  onUpdate?: (variable: FlowVariable) => void;
  onDelete?: (variableId: string) => void;
}

interface VariableFormData {
  name: string;
  apiName: string;
  type: VariableType;
  objectType?: string;
  defaultValue?: string;
  isInput: boolean;
  isOutput: boolean;
  isCollection: boolean;
  description?: string;
}

// ============================================
// CONSTANTS
// ============================================

const VARIABLE_TYPES: { value: VariableType; label: string; icon: React.ReactNode }[] = [
  { value: 'text', label: 'Text', icon: <Type size={16} /> },
  { value: 'number', label: 'Number', icon: <Hash size={16} /> },
  { value: 'currency', label: 'Currency', icon: <DollarSign size={16} /> },
  { value: 'date', label: 'Date', icon: <Calendar size={16} /> },
  { value: 'datetime', label: 'Date/Time', icon: <Calendar size={16} /> },
  { value: 'boolean', label: 'Boolean', icon: <ToggleLeft size={16} /> },
  { value: 'picklist', label: 'Picklist', icon: <List size={16} /> },
  { value: 'record', label: 'Record', icon: <Database size={16} /> },
  { value: 'collection', label: 'Collection', icon: <Layers size={16} /> }
];

const OBJECT_TYPES = [
  { value: 'Client', label: 'Client' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Task', label: 'Task' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Account', label: 'Account' },
  { value: 'CreditReport', label: 'Credit Report' },
  { value: 'CyberSweepClient', label: 'CyberSweep Client' }
];

const DEFAULT_FORM_DATA: VariableFormData = {
  name: '',
  apiName: '',
  type: 'text',
  objectType: undefined,
  defaultValue: '',
  isInput: false,
  isOutput: false,
  isCollection: false,
  description: ''
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getTypeIcon = (type: VariableType) => {
  const typeDef = VARIABLE_TYPES.find(t => t.value === type);
  return typeDef?.icon || <Type size={16} />;
};

const getTypeLabel = (type: VariableType) => {
  const typeDef = VARIABLE_TYPES.find(t => t.value === type);
  return typeDef?.label || type;
};

const getTypeColor = (type: VariableType): string => {
  switch (type) {
    case 'text':
      return 'text-blue-500';
    case 'number':
      return 'text-green-500';
    case 'currency':
      return 'text-emerald-500';
    case 'date':
    case 'datetime':
      return 'text-purple-500';
    case 'boolean':
      return 'text-orange-500';
    case 'picklist':
      return 'text-pink-500';
    case 'record':
      return 'text-cyan-500';
    case 'collection':
      return 'text-indigo-500';
    default:
      return 'text-gray-500';
  }
};

// ============================================
// VARIABLE CARD COMPONENT
// ============================================

interface VariableCardProps {
  variable: FlowVariable;
  readOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const VariableCard: React.FC<VariableCardProps> = ({
  variable,
  readOnly,
  onEdit,
  onDelete
}) => {
  return (
    <StandardCard className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Type Icon */}
        <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-700 ${getTypeColor(variable.type)}`}>
          {getTypeIcon(variable.type)}
        </div>

        {/* Variable Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {variable.name}
            </h4>
            {variable.isInput && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded">
                <ArrowDownToLine size={10} />
                Input
              </span>
            )}
            {variable.isOutput && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded">
                <ArrowUpFromLine size={10} />
                Output
              </span>
            )}
            {variable.isCollection && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded">
                <Layers size={10} />
                Collection
              </span>
            )}
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mb-1">
            {variable.apiName}
          </p>

          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              {getTypeIcon(variable.type)}
              {getTypeLabel(variable.type)}
            </span>
            {variable.objectType && (
              <span>• {variable.objectType}</span>
            )}
            {variable.defaultValue !== undefined && variable.defaultValue !== '' && (
              <span>• Default: {String(variable.defaultValue)}</span>
            )}
          </div>

          {variable.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {variable.description}
            </p>
          )}
        </div>

        {/* Actions */}
        {!readOnly && (
          <div className="flex items-center gap-1">
            <StandardButton
              variant="ghost"
              size="sm"
              onClick={onEdit}
              title="Edit variable"
            >
              <Edit2 size={14} />
            </StandardButton>
            <StandardButton
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-500 hover:text-red-600"
              title="Delete variable"
            >
              <Trash2 size={14} />
            </StandardButton>
          </div>
        )}
      </div>
    </StandardCard>
  );
};

// ============================================
// VARIABLE MODAL COMPONENT
// ============================================

interface VariableModalProps {
  isOpen: boolean;
  onClose: () => void;
  variable?: FlowVariable;
  onSave: (data: VariableFormData) => void;
}

const VariableModal: React.FC<VariableModalProps> = ({
  isOpen,
  onClose,
  variable,
  onSave
}) => {
  const [formData, setFormData] = useState<VariableFormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (variable) {
        setFormData({
          name: variable.name,
          apiName: variable.apiName,
          type: variable.type,
          objectType: variable.objectType,
          defaultValue: variable.defaultValue ? String(variable.defaultValue) : '',
          isInput: variable.isInput,
          isOutput: variable.isOutput,
          isCollection: variable.isCollection,
          description: variable.description || ''
        });
      } else {
        setFormData(DEFAULT_FORM_DATA);
      }
      setErrors({});
    }
  }, [isOpen, variable]);

  // Auto-generate API name from name
  const handleNameChange = (name: string) => {
    const apiName = name
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .toLowerCase();

    setFormData(prev => ({
      ...prev,
      name,
      apiName: prev.apiName || apiName
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Variable name is required';
    }

    if (!formData.apiName.trim()) {
      newErrors.apiName = 'API name is required';
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(formData.apiName)) {
      newErrors.apiName = 'API name must start with a letter and contain only letters, numbers, and underscores';
    }

    if ((formData.type === 'record' || formData.type === 'collection') && !formData.objectType) {
      newErrors.objectType = 'Object type is required for record/collection variables';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title={variable ? 'Edit Variable' : 'Add Variable'}
      size="md"
    >
      <div className="p-6 space-y-4">
        <StandardInput
          label="Variable Name"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Account Name"
          required
          error={errors.name}
        />

        <StandardInput
          label="API Name"
          value={formData.apiName}
          onChange={(e) => setFormData({ ...formData, apiName: e.target.value })}
          placeholder="e.g., account_name"
          required
          error={errors.apiName}
          helpText="Used for programmatic access"
        />

        <StandardSelect
          label="Data Type"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as VariableType })}
          options={VARIABLE_TYPES.map(t => ({ value: t.value, label: t.label }))}
        />

        {(formData.type === 'record' || formData.type === 'collection') && (
          <StandardSelect
            label="Object Type"
            value={formData.objectType || ''}
            onChange={(e) => setFormData({ ...formData, objectType: e.target.value })}
            options={[{ value: '', label: 'Select Object' }, ...OBJECT_TYPES]}
            required
            error={errors.objectType}
          />
        )}

        <StandardInput
          label="Default Value"
          value={formData.defaultValue || ''}
          onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
          placeholder="Optional default value"
          type={formData.type === 'number' || formData.type === 'currency' ? 'number' : 'text'}
        />

        <div className="grid grid-cols-3 gap-4">
          <StandardCheckbox
            label="Available for Input"
            checked={formData.isInput}
            onChange={(checked) => setFormData({ ...formData, isInput: checked })}
          />
          <StandardCheckbox
            label="Available for Output"
            checked={formData.isOutput}
            onChange={(checked) => setFormData({ ...formData, isOutput: checked })}
          />
          <StandardCheckbox
            label="Is Collection"
            checked={formData.isCollection}
            onChange={(checked) => setFormData({ ...formData, isCollection: checked })}
          />
        </div>

        <StandardTextarea
          label="Description"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Optional description of what this variable is used for"
          rows={2}
        />
      </div>

      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
        <StandardButton variant="secondary" onClick={onClose}>
          Cancel
        </StandardButton>
        <StandardButton variant="primary" onClick={handleSubmit}>
          {variable ? 'Save Changes' : 'Add Variable'}
        </StandardButton>
      </div>
    </StandardModal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const VariableManager: React.FC<VariableManagerProps> = ({
  variables,
  readOnly = false,
  onAdd,
  onUpdate,
  onDelete
}) => {
  const { addToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState<FlowVariable | undefined>();
  const [filter, setFilter] = useState<'all' | 'input' | 'output'>('all');

  // Filter variables
  const filteredVariables = variables.filter(v => {
    if (filter === 'input') return v.isInput;
    if (filter === 'output') return v.isOutput;
    return true;
  });

  // Group variables by type
  const groupedVariables = filteredVariables.reduce((groups, variable) => {
    const group = groups[variable.type] || [];
    group.push(variable);
    groups[variable.type] = group;
    return groups;
  }, {} as Record<VariableType, FlowVariable[]>);

  const handleAdd = () => {
    setEditingVariable(undefined);
    setShowModal(true);
  };

  const handleEdit = (variable: FlowVariable) => {
    setEditingVariable(variable);
    setShowModal(true);
  };

  const handleDelete = (variableId: string) => {
    const variable = variables.find(v => v.id === variableId);
    if (window.confirm(`Are you sure you want to delete "${variable?.name}"?`)) {
      onDelete?.(variableId);
      addToast({
        type: 'success',
        title: 'Variable Deleted',
        message: `"${variable?.name}" has been deleted`
      });
    }
  };

  const handleSave = (data: VariableFormData) => {
    if (editingVariable) {
      const updated: FlowVariable = {
        ...editingVariable,
        ...data,
        defaultValue: data.defaultValue || undefined
      };
      onUpdate?.(updated);
      addToast({
        type: 'success',
        title: 'Variable Updated',
        message: `"${data.name}" has been updated`
      });
    } else {
      const newVariable: FlowVariable = {
        id: `var_${Date.now()}`,
        ...data,
        defaultValue: data.defaultValue || undefined
      };
      onAdd?.(newVariable);
      addToast({
        type: 'success',
        title: 'Variable Added',
        message: `"${data.name}" has been added`
      });
    }
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Variables
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {variables.length} variable{variables.length !== 1 ? 's' : ''} defined
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filter */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {(['all', 'input', 'output'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  filter === f
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {!readOnly && (
            <StandardButton
              variant="primary"
              onClick={handleAdd}
              leftIcon={<Plus size={16} />}
            >
              Add Variable
            </StandardButton>
          )}
        </div>
      </div>

      {/* Variables List */}
      {filteredVariables.length === 0 ? (
        <StandardEmptyState
          title="No Variables"
          message={
            filter !== 'all'
              ? `No ${filter} variables defined. Switch to "All" to see all variables.`
              : readOnly
              ? "This flow doesn't have any variables defined."
              : "No variables defined yet. Add variables to store data in your flow."
          }
          actionLabel={!readOnly && filter === 'all' ? 'Add Variable' : undefined}
          onAction={!readOnly && filter === 'all' ? handleAdd : undefined}
        />
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedVariables).map(([type, vars]) => (
            <div key={type}>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <span className={getTypeColor(type as VariableType)}>
                  {getTypeIcon(type as VariableType)}
                </span>
                {getTypeLabel(type as VariableType)} Variables ({vars.length})
              </h4>
              <div className="grid gap-3">
                {vars.map(variable => (
                  <VariableCard
                    key={variable.id}
                    variable={variable}
                    readOnly={readOnly}
                    onEdit={() => handleEdit(variable)}
                    onDelete={() => handleDelete(variable.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {variables.length > 0 && (
        <StandardCard className="p-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <ArrowDownToLine size={14} className="text-blue-500" />
              <span className="text-gray-600 dark:text-gray-400">
                {variables.filter(v => v.isInput).length} Input Variables
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpFromLine size={14} className="text-green-500" />
              <span className="text-gray-600 dark:text-gray-400">
                {variables.filter(v => v.isOutput).length} Output Variables
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Layers size={14} className="text-purple-500" />
              <span className="text-gray-600 dark:text-gray-400">
                {variables.filter(v => v.isCollection).length} Collections
              </span>
            </div>
          </div>
        </StandardCard>
      )}

      {/* Variable Modal */}
      <VariableModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        variable={editingVariable}
        onSave={handleSave}
      />
    </div>
  );
};

export default VariableManager;
